/** Split / pack copy across a fixed number of carousel slides. */

function splitIntoSentences(text) {
  const normalized = String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();
  if (!normalized) return [];

  const chunks = normalized
    .split(/\n+/)
    .flatMap((para) => {
      const trimmed = para.trim();
      if (!trimmed) return [];
      const parts = trimmed.match(/[^.!?…]+[.!?…]+|[^.!?…]+$/g);
      return (parts || [trimmed]).map((s) => s.trim()).filter(Boolean);
    });

  return chunks.length > 0 ? chunks : [normalized];
}

/**
 * Pack sentences into `slideCount` slides with roughly balanced length.
 * Falls back to word packing when there aren't enough sentences.
 */
export function splitTextIntoSlides(text, slideCount) {
  const n = Math.max(1, Math.min(20, Math.round(Number(slideCount) || 1)));
  const raw = String(text || '').trim();
  if (!raw) return Array.from({ length: n }, () => '');

  let units = splitIntoSentences(raw);
  if (units.length < n) {
    const words = raw.split(/\s+/).filter(Boolean);
    if (words.length >= n) {
      units = words;
    }
  }

  if (units.length <= n) {
    const slides = Array.from({ length: n }, (_, i) => units[i] || '');
    // If we had fewer units than slides, fold leftover capacity by leaving empties
    // only at the end — prefer filling earlier slides when possible.
    return slides;
  }

  const totalLen = units.reduce((acc, u) => acc + u.length + 1, 0);
  const target = totalLen / n;
  const slides = [];
  let bucket = [];
  let bucketLen = 0;

  for (let i = 0; i < units.length; i++) {
    const unit = units[i];
    const remainingUnits = units.length - i;
    const remainingSlots = n - slides.length;
    const wouldBe = bucketLen + unit.length + (bucket.length ? 1 : 0);
    const mustClose = remainingUnits <= remainingSlots - (bucket.length ? 0 : 1);

    if (bucket.length > 0 && (wouldBe > target * 1.15 || mustClose) && slides.length < n - 1) {
      slides.push(bucket.join(' ').trim());
      bucket = [unit];
      bucketLen = unit.length;
    } else {
      bucket.push(unit);
      bucketLen = wouldBe;
    }
  }
  if (bucket.length) slides.push(bucket.join(' ').trim());

  while (slides.length < n) slides.push('');
  if (slides.length > n) {
    const head = slides.slice(0, n - 1);
    const tail = slides.slice(n - 1).join(' ').trim();
    return [...head, tail];
  }
  return slides;
}

/** Join slide texts then re-pack into a new slide count. */
export function redistributeSlideTexts(texts, newCount) {
  const combined = (Array.isArray(texts) ? texts : [])
    .map((t) => String(t || '').trim())
    .filter(Boolean)
    .join(' ');
  return splitTextIntoSlides(combined, newCount);
}

export function newSlideId() {
  return `slide-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
